import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { ArrowLeft, Download, FileText, Loader2 } from "lucide-react";

const OPERATORS = [
  { value: "addition", label: "Addition Only" },
  { value: "addition_subtraction", label: "Addition & Subtraction" },
  { value: "multiplication", label: "Multiplication" },
  { value: "division", label: "Division" }
];
const QUESTION_COUNTS = [5, 10, 15, 20, 25, 50];
const ROW_COUNTS = [2, 3, 4, 5, 6, 7, 8];
const DIGIT_COUNTS = [1, 2, 3, 4, 5];

export default function WorksheetGenerator() {
  const navigate = useNavigate();
  const [generating, setGenerating] = useState(false);
  const [config, setConfig] = useState({
    operator: "addition",
    num_questions: 10,
    num_rows: 3,
    num_digits: 2,
    student_name: "",
    include_answer_key: true
  });

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const response = await axios.post(`${API}/worksheet/generate`, config, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `abacus_worksheet_${config.operator}_${config.num_digits}d_${config.num_rows}r.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Worksheet downloaded!");
    } catch (error) {
      toast.error("Failed to generate worksheet");
    } finally {
      setGenerating(false);
    }
  };

  const update = (key, value) => setConfig(prev => ({ ...prev, [key]: value }));

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-background/95 backdrop-blur sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 h-14 flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate("/")} className="rounded-full" data-testid="back-btn">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div className="flex items-center gap-2">
            <img src="/logo.png" alt="TheMatheMind 360" className="h-7 rounded" />
            <div className="flex flex-col">
              <span className="text-sm font-bold font-['Manrope'] leading-tight">TheMatheMind <span className="text-amber-600">360</span></span>
              <span className="text-[9px] text-muted-foreground leading-tight tracking-wider uppercase">A 360&deg; Learning Platform</span>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-10">
        <div className="text-center mb-10">
          <h1 className="text-3xl font-bold font-['Manrope'] text-foreground mb-2" data-testid="page-title">
            Abacus Worksheet Generator
          </h1>
          <p className="text-muted-foreground">Generate custom math worksheets for abacus practice. Free to use!</p>
        </div>

        <Card className="border-border/50 shadow-lg">
          <CardHeader>
            <CardTitle className="text-lg font-['Manrope']">Configure Your Worksheet</CardTitle>
            <CardDescription>Customize the questions and download as PDF</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Operator */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Operator</Label>
              <div className="grid grid-cols-2 gap-2" data-testid="operator-options">
                {OPERATORS.map((op) => (
                  <button
                    key={op.value}
                    type="button"
                    className={`px-4 py-3 rounded-xl border text-sm font-medium transition-all ${
                      config.operator === op.value
                        ? "border-primary bg-primary/5 text-primary shadow-sm"
                        : "border-border text-muted-foreground hover:border-primary/30 hover:text-foreground"
                    }`}
                    onClick={() => update("operator", op.value)}
                    data-testid={`operator-${op.value}`}
                  >
                    {op.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-3 gap-6">
              {/* Number of Questions */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">No. of Questions</Label>
                <Select value={String(config.num_questions)} onValueChange={(v) => update("num_questions", parseInt(v))}>
                  <SelectTrigger className="rounded-xl" data-testid="questions-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {QUESTION_COUNTS.map((n) => (
                      <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Number of Rows */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">No. of Rows</Label>
                <Select value={String(config.num_rows)} onValueChange={(v) => update("num_rows", parseInt(v))}>
                  <SelectTrigger className="rounded-xl" data-testid="rows-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ROW_COUNTS.map((n) => (
                      <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Number of Digits */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">No. of Digits</Label>
                <Select value={String(config.num_digits)} onValueChange={(v) => update("num_digits", parseInt(v))}>
                  <SelectTrigger className="rounded-xl" data-testid="digits-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DIGIT_COUNTS.map((n) => (
                      <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Student Name */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Student Name <span className="text-muted-foreground font-normal">(optional)</span></Label>
              <Input
                data-testid="student-name-input"
                placeholder="Enter student name"
                className="rounded-xl"
                value={config.student_name}
                onChange={(e) => update("student_name", e.target.value)}
              />
            </div>

            {/* Answer Key Toggle */}
            <div className="flex items-center justify-between p-4 rounded-xl border border-border bg-muted/20">
              <div>
                <Label className="text-sm font-medium">Include Answer Key</Label>
                <p className="text-xs text-muted-foreground mt-0.5">Adds answers on a separate page</p>
              </div>
              <Switch
                data-testid="answer-key-toggle"
                checked={config.include_answer_key}
                onCheckedChange={(v) => update("include_answer_key", v)}
              />
            </div>

            {/* Generate Button */}
            <Button
              data-testid="generate-btn"
              className="w-full h-12 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 text-base font-medium"
              onClick={handleGenerate}
              disabled={generating}
            >
              {generating ? (
                <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Generating...</>
              ) : (
                <><Download className="w-5 h-5 mr-2" />Download Worksheet (PDF)</>
              )}
            </Button>

            {/* Preview Info */}
            <div className="text-center p-4 rounded-xl bg-muted/30 border border-border/50">
              <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                <FileText className="w-4 h-4" />
                <span>
                  {config.num_questions} questions • {config.num_digits}-digit numbers • {config.num_rows} rows • {OPERATORS.find(o => o.value === config.operator)?.label}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
